// Plan 14-04 — LoadoutPickerModal (MUI-03 pre-match 4-of-8 loadout picker).
//
// Surfaces:
//   - mode='casual': read-only badge — DEC-024 default 4 (scriveners_quill,
//     tongue_tie, knights_vow, whisperers_promise) — Lock-in CTA fires with
//     the verbatim default tuple (no edits per CONTEXT lock).
//   - mode='ranked' | mode='friend': interactive 8-card grid; user toggles 4;
//     Lock-in CTA gated on selected.size===4; persists via setLoadoutForMode.
//   - availablePowerups.length===0 → empty-state copy + Cancel CTA only.
//
// FSM (ranked/friend):
//   initial:
//     selected ← getLoadoutForMode(mode) (filtered to availablePowerups) — if
//     null, selected ← ∅ (fresh; user picks from scratch)
//   toggle(card):
//     selected.has(card) → selected.delete(card)
//     selected.size < 4 → selected.add(card)
//     selected.size===4 + !selected.has(card) → no-op (button is disabled)
//   confirm():
//     setLoadoutForMode(mode, selected as Loadout); onLockIn(selected)
//
// Pattern S1 (role=dialog + aria-modal=true) — mirrors EmpowerBottomSheet
// + DeleteAccountModal frame. Sun-Yellow 3px border on selected cards;
// surface-stroke 2px on unselected (UI-SPEC Visual Language).
//
// DEC-021 (server authority) preservation:
//   The picker filters the persisted preset against availablePowerups at
//   seed-time AND disables cards not in availablePowerups; the backend's
//   match-create handler is the runtime gate — a tampered localStorage
//   value either fails the disabled-button gate here OR (worst case) is
//   rejected at submitTurn time by validateTurn (see 14-PATTERNS Pitfall 7).

import { type JSX, useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import type { PowerUpName } from '../../game/PlayerViewSchema'
import {
  getLoadoutForMode,
  type Loadout,
  setLoadoutForMode,
} from '../../stores/loadoutPresets'
import { Btn } from '../components/Btn'
import { Sheet } from '../components/Sheet'

export interface LoadoutPickerModalProps {
  readonly open: boolean
  readonly mode: 'casual' | 'ranked' | 'friend'
  readonly availablePowerups: readonly PowerUpName[]
  readonly onLockIn: (loadout: readonly PowerUpName[]) => void
  readonly onCancel: () => void
  /**
   * POWERUP-02 — lock-in CTA copy key. Defaults to the loadout lock-in label;
   * the first-casual read-only preview passes a "find a match" framing key.
   */
  readonly ctaLabelId?: string
  /**
   * POWERUP-02 — header title key override (default 'Choose 4 power-ups'). The
   * read-only first-casual preview passes 'Your power-ups' so the header isn't
   * the misleading "choose" framing.
   */
  readonly titleId?: string
  /**
   * POWERUP-02 — optional body paragraph rendered above the casual badge (the
   * preview uses it to teach "tap any in a match to use it"). Omitted = no extra
   * paragraph (existing behavior).
   */
  readonly bodyId?: string
}

// DEC-024 lock — do not modify. The four casual-default power-ups
// (canonical order). Casual mode renders this verbatim.
export const CASUAL_DEFAULT_LOADOUT: readonly PowerUpName[] = [
  'scriveners_quill',
  'tongue_tie',
  'knights_vow',
  'whisperers_promise',
]

// All 8 power-ups in display order (4-of-8 = 4x2 grid). Mirrors
// PowerUpName union in PlayerViewSchema.ts.
const ALL_POWERUPS: readonly PowerUpName[] = [
  'scriveners_quill',
  'tongue_tie',
  'knights_vow',
  'whisperers_promise',
  'lexical_mulligan',
  'the_vanishing',
  'castle_keep',
  'anagram_sigil',
]

export function LoadoutPickerModal({
  open,
  mode,
  availablePowerups,
  onLockIn,
  onCancel,
  ctaLabelId = 'match.loadout.cta.lock_in',
  titleId = 'match.loadout.modal.title',
  bodyId,
}: LoadoutPickerModalProps): JSX.Element | null {
  const intl = useIntl()
  const [selected, setSelected] = useState<ReadonlySet<PowerUpName>>(
    () => new Set(),
  )

  // Seed `selected` from the persisted preset when the modal opens (or when
  // the mode changes). The seed is filtered against availablePowerups so a
  // stale localStorage preset containing a deprecated power-up name never
  // appears as "selected" (DEC-021 server authority).
  useEffect(() => {
    if (!open) return
    if (mode === 'casual') {
      // Casual mode renders the read-only badge — the selected set is the
      // DEC-024 default verbatim (filtered defensively to availablePowerups).
      setSelected(
        new Set(
          CASUAL_DEFAULT_LOADOUT.filter((p) => availablePowerups.includes(p)),
        ),
      )
      return
    }
    const persisted = getLoadoutForMode(mode)
    if (persisted === null) {
      setSelected(new Set())
      return
    }
    const filtered = persisted.filter((p) => availablePowerups.includes(p))
    setSelected(new Set(filtered))
  }, [open, mode, availablePowerups])

  if (!open) return null

  function handleToggle(name: PowerUpName): void {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(name)) {
        next.delete(name)
        return next
      }
      // Hard cap at 4 — silently no-op if the user somehow clicks a
      // disabled button (the button is disabled in this state, so this
      // branch defends against keyboard / programmatic invocation).
      if (next.size >= 4) return prev
      next.add(name)
      return next
    })
  }

  function handleLockInCasual(): void {
    onLockIn(CASUAL_DEFAULT_LOADOUT)
  }

  function handleLockInPicker(): void {
    if (selected.size !== 4) return
    // IN-03 fix (Phase 14 fix-pass iter 1): construct the Loadout tuple
    // explicitly so TypeScript's tuple-length check actually applies.
    // The pre-fix `Array.from(selected) as readonly PowerUpName[]` →
    // `as unknown as Loadout` double-cast bypassed the length guarantee;
    // a future refactor returning a 3- or 5-element array would have
    // type-checked silently and written a malformed preset to
    // localStorage. Destructure-with-guard pattern avoids non-null
    // assertions (biome lint/style/noNonNullAssertion); the inner check
    // is defensive — `selected.size === 4` already implies all four
    // entries are non-undefined, but the guard is what carries that
    // proof through the type-flow.
    const [a, b, c, d] = Array.from(selected)
    if (
      a === undefined ||
      b === undefined ||
      c === undefined ||
      d === undefined
    ) {
      return
    }
    const loadout: Loadout = [a, b, c, d]
    // Persist BEFORE firing onLockIn so the consumer can rely on the
    // localStorage value being current if it round-trips immediately.
    setLoadoutForMode(mode, loadout)
    onLockIn(loadout)
  }

  const isEmpty = availablePowerups.length === 0
  const isCasual = mode === 'casual'
  const isPicker = mode === 'ranked' || mode === 'friend'

  return (
    // Bottom-sheet flavor (UI-SPEC §5): scrim + grab handle + focus trap +
    // scrim-tap/Escape dismissal all live in <Sheet>/ModalScrim.
    // onCancel maps to onClose — the Escape + scrim-tap dismiss path.
    <Sheet
      onClose={onCancel}
      testId="loadout-picker-modal"
      labelledBy="loadout-modal-title"
    >
      {/* Header band */}
      <div
        style={{
          height: '48px',
          backgroundColor: 'var(--color-surface-deep)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          borderBottom: '3px solid var(--color-surface-stroke)',
        }}
      >
        <h2
          id="loadout-modal-title"
          style={{
            margin: 0,
            fontFamily: 'var(--font-family-display)',
            fontWeight: 700,
            fontSize: '18px',
            color: 'var(--color-text-primary)',
          }}
        >
          <FormattedMessage
            id={isEmpty ? 'match.loadout.empty.title' : titleId}
          />
        </h2>
      </div>

      {/* Body — branches on isEmpty / isCasual / isPicker */}
      <div style={{ padding: '20px' }}>
        {isEmpty ? (
          <>
            <p
              style={{
                margin: 0,
                fontFamily: 'var(--font-family-body)',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: 1.5,
                color: 'var(--color-text-primary)',
              }}
            >
              <FormattedMessage id="match.loadout.empty.body" />
            </p>
            <div
              style={{
                marginTop: '20px',
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Btn kind="ghost" dataCta="loadout-cancel" onClick={onCancel}>
                <FormattedMessage id="match.loadout.cta.cancel" />
              </Btn>
            </div>
          </>
        ) : isCasual ? (
          <>
            {/* POWERUP-02 — optional teaching paragraph (the first-casual
                preview uses it to explain how power-ups are used). */}
            {bodyId !== undefined ? (
              <p
                data-screen-region="loadout-casual-preview-body"
                style={{
                  margin: '0 0 12px',
                  fontFamily: 'var(--font-family-body)',
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: 1.5,
                  color: 'var(--color-text-primary)',
                }}
              >
                <FormattedMessage id={bodyId} />
              </p>
            ) : null}
            <div
              role="status"
              data-screen-region="loadout-casual-badge"
              style={{
                padding: '10px 12px',
                borderRadius: '8px',
                backgroundColor: 'var(--color-surface-deep)',
                border: '2px solid var(--color-surface-stroke)',
                color: 'var(--color-text-primary)',
                fontFamily: 'var(--font-family-body)',
                fontWeight: 500,
                fontSize: '14px',
              }}
            >
              <FormattedMessage id="match.loadout.casual_default" />
            </div>
            <ul
              style={{
                margin: '12px 0 0',
                padding: '0',
                listStyle: 'none',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              {CASUAL_DEFAULT_LOADOUT.map((p) => (
                <li
                  key={p}
                  style={{
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '3px solid var(--color-cta-primary)',
                    backgroundColor: 'var(--color-surface-deep)',
                    color: 'var(--color-text-primary)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--space-1)',
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'var(--font-family-display)',
                      fontWeight: 700,
                      fontSize: '14px',
                    }}
                  >
                    <FormattedMessage id={`match.powerup.name.${p}`} />
                  </span>
                  {/* POWERUP-02 / POWERUP-03 parity — the casual loadout is
                      self-documenting: the existing effect line per power-up. */}
                  <span
                    data-powerup-desc={p}
                    style={{
                      fontFamily: 'var(--font-family-body)',
                      fontWeight: 500,
                      fontSize: 'var(--font-size-12)',
                      lineHeight: 1.4,
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    <FormattedMessage id={`match.powerup.desc.${p}`} />
                  </span>
                </li>
              ))}
            </ul>
            <div style={{ marginTop: '20px' }}>
              <Btn
                kind="primary"
                size="lg"
                block
                dataCta="loadout-lock-in-casual"
                onClick={handleLockInCasual}
              >
                <FormattedMessage id={ctaLabelId} />
              </Btn>
            </div>
          </>
        ) : isPicker ? (
          <>
            <p
              style={{
                margin: 0,
                fontFamily: 'var(--font-family-body)',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: 1.5,
                color: 'var(--color-text-primary)',
              }}
            >
              <FormattedMessage id="match.loadout.modal.body" />
            </p>
            <fieldset
              aria-label={intl.formatMessage({
                id: 'match.loadout.power_up_cards_aria',
              })}
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '8px',
                marginTop: '16px',
                border: 'none',
                padding: 0,
                margin: '16px 0 0',
              }}
            >
              {ALL_POWERUPS.map((p) => {
                const isSelected = selected.has(p)
                const isAvailable = availablePowerups.includes(p)
                const fullDisabled =
                  !isAvailable || (!isSelected && selected.size >= 4)
                const nameLabel = intl.formatMessage({
                  id: `match.powerup.name.${p}`,
                })
                return (
                  <button
                    key={p}
                    type="button"
                    data-powerup-card={p}
                    aria-pressed={isSelected ? 'true' : 'false'}
                    aria-disabled={!isAvailable ? 'true' : 'false'}
                    aria-label={nameLabel}
                    disabled={fullDisabled}
                    onClick={() => handleToggle(p)}
                    style={{
                      minWidth: '64px',
                      minHeight: '64px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '12px 8px',
                      border: isSelected
                        ? '3px solid var(--color-cta-primary)'
                        : '2px solid var(--color-surface-stroke)',
                      backgroundColor: 'var(--color-surface-mid)',
                      color: 'var(--color-text-primary)',
                      borderRadius: '8px',
                      cursor: fullDisabled ? 'not-allowed' : 'pointer',
                      opacity: !isAvailable ? 0.45 : 1,
                      fontFamily: 'var(--font-family-display)',
                      fontWeight: 700,
                      fontSize: '12px',
                      textAlign: 'center',
                    }}
                  >
                    <FormattedMessage id={`match.powerup.name.${p}`} />
                  </button>
                )
              })}
            </fieldset>
            <div style={{ marginTop: '20px' }}>
              <Btn
                kind="primary"
                size="lg"
                block
                dataCta="loadout-lock-in-picker"
                disabled={selected.size !== 4}
                onClick={handleLockInPicker}
              >
                <FormattedMessage id="match.loadout.cta.lock_in" />
              </Btn>
            </div>
          </>
        ) : null}
      </div>
    </Sheet>
  )
}
